/**
 * LocationsSettings — Locations list card + Add / Edit / Delete dialogs.
 * All state and handlers live in Settings.js (parent). This component is pure JSX.
 * RULE: Do NOT add state here. Pass new props from Settings.js instead.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Plus, Pencil, Trash2, Store, MapPin, Key, Copy,
  Upload, Image, ImageIcon, Phone, Mail, DollarSign,
  Target, AlertCircle, Loader2,
} from 'lucide-react';

/** Resolves a relative image URL to an absolute URL using the backend base. */
function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function LocationsSettings({
  user,
  restaurants,
  // dialog open state
  dialogOpen,
  setDialogOpen,
  editDialogOpen,
  setEditDialogOpen,
  deleteDialogOpen,
  setDeleteDialogOpen,
  // location-level state
  locationName,
  setLocationName,
  editingLocation,
  setEditingLocation,
  editForm,
  setEditForm,
  logoFile,
  setLogoFile,
  logoPreview,
  setLogoPreview,
  iconFile,
  setIconFile,
  iconPreview,
  setIconPreview,
  fileInputRef,
  iconInputRef,
  deletingLocation,
  setDeletingLocation,
  deletePinInput,
  setDeletePinInput,
  deletePinError,
  setDeletePinError,
  // shared
  submitting,
  // handlers (all defined in Settings.js)
  handleAddLocation,
  openEditDialog,
  handleEditLocation,
  handleDeleteLogo,
  handleDeleteIcon,
  handleLogoSelect,
  handleIconSelect,
  copyLocationPin,
  handleDeleteLocation,
}) {
  return (
    <>
      {/* Locations Card — data-settings-section="locations" (REQUIRED — do not remove) */}
      <Card className="border-slate-200" data-settings-section="locations">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Store size={20} className="text-slate-600" />
              Locations
            </CardTitle>
            <CardDescription>
              {restaurants.length} location{restaurants.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="add-location-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Location
          </Button>
        </CardHeader>
        <CardContent>
          {restaurants.length === 0 ? (
            <p className="text-center py-4 text-slate-500">No locations yet</p>
          ) : (
            <div className="space-y-3">
              {restaurants.map((restaurant) => (
                <div
                  key={restaurant.id}
                  className="p-4 bg-slate-50 rounded-lg"
                  data-testid={`location-${restaurant.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Logo */}
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden bg-orange-100 flex-shrink-0">
                        {restaurant.logo_url ? (
                          <img
                            src={resolveImageUrl(restaurant.logo_url)}
                            alt={restaurant.location_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.onerror = null; e.target.src = '/images/placeholder-logo.png'; }}
                          />
                        ) : (
                          <Store className="text-orange-600" size={24} />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900">{restaurant.location_name}</p>
                        {restaurant.address && (
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                            <MapPin size={12} />
                            <span className="truncate">{restaurant.address}</span>
                          </p>
                        )}
                        {/* Location PIN */}
                        {restaurant.location_pin && (
                          <div className="flex items-center gap-2 mt-1.5">
                            <Key className="h-3.5 w-3.5 text-orange-500" />
                            <span className="text-sm font-medium text-slate-700">PIN:</span>
                            <span
                              className="font-mono font-bold text-orange-600 tracking-wider text-base"
                              data-testid={`location-pin-${restaurant.id}`}
                            >
                              {restaurant.location_pin}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyLocationPin(restaurant.location_pin)}
                              title="Copy PIN"
                              className="h-6 w-6 p-0 ml-1"
                              data-testid={`copy-location-pin-${restaurant.id}`}
                            >
                              <Copy className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600" />
                            </Button>
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {restaurant.phone && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Phone size={10} />
                              {restaurant.phone}
                            </span>
                          )}
                          {restaurant.email && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Mail size={10} />
                              {restaurant.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user?.role === 'owner' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDeletingLocation(restaurant);
                            setDeleteDialogOpen(true);
                          }}
                          title="Delete Location"
                          data-testid={`delete-location-${restaurant.id}`}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(restaurant)}
                        data-testid={`edit-location-${restaurant.id}`}
                      >
                        <Pencil size={16} className="text-slate-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Location Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Location</DialogTitle>
            <DialogDescription>
              Add a new location to your group
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddLocation} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="locationName">Location Name</Label>
              <Input
                id="locationName"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder="e.g., Downtown Branch"
                required
                data-testid="location-name-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={submitting}
                data-testid="add-location-submit-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Location'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
            <DialogDescription>
              Update your location details and branding
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditLocation} className="space-y-5">
            {/* Images Section */}
            <div className="grid grid-cols-2 gap-4">
              {/* Logo Section */}
              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/images/placeholder-logo.png'; }}
                      />
                    ) : (
                      <Image className="text-slate-400" size={28} />
                    )}
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoSelect}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={14} className="mr-1" />
                      Upload
                    </Button>
                    {logoPreview && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleDeleteLogo}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full"
                        disabled={submitting}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center">Max 2MB</p>
              </div>

              {/* Icon Section */}
              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
                    {iconPreview ? (
                      <img
                        src={iconPreview}
                        alt="Icon preview"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/images/placeholder-icon.png'; }}
                      />
                    ) : (
                      <ImageIcon className="text-slate-400" size={28} />
                    )}
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                    <input
                      type="file"
                      ref={iconInputRef}
                      onChange={handleIconSelect}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => iconInputRef.current?.click()}
                    >
                      <Upload size={14} className="mr-1" />
                      Upload
                    </Button>
                    {iconPreview && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleDeleteIcon}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full"
                        disabled={submitting}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center">Max 1MB (square)</p>
              </div>
            </div>

            {/* Location Name */}
            <div className="space-y-2">
              <Label htmlFor="edit-location-name">Location Name *</Label>
              <Input
                id="edit-location-name"
                value={editForm.location_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, location_name: e.target.value }))}
                placeholder="e.g., Downtown Branch"
                required
                data-testid="edit-location-name-input"
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Textarea
                id="edit-address"
                value={editForm.address}
                onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="123 Main St, City, State 12345"
                rows={2}
                data-testid="edit-address-input"
              />
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    id="edit-phone"
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="pl-9"
                    data-testid="edit-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    id="edit-email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@restaurant.com"
                    className="pl-9"
                    data-testid="edit-email-input"
                  />
                </div>
              </div>
            </div>

            {/* Labor Budget Configuration */}
            <div className="space-y-4 border-t border-slate-200 pt-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign size={16} className="text-orange-500" />
                <Label className="text-sm font-semibold text-slate-800">Labor Budget</Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-budget-type" className="text-xs text-slate-600">Calculation Method</Label>
                <Select
                  value={editForm.budget_type}
                  onValueChange={(val) => setEditForm(prev => ({ ...prev, budget_type: val }))}
                >
                  <SelectTrigger data-testid="edit-budget-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual (fixed weekly budget)</SelectItem>
                    <SelectItem value="revenue_based">Revenue-Based (% of revenue)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-labor-target" className="text-xs text-slate-600">Labor Target %</Label>
                <div className="relative">
                  <Target className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    id="edit-labor-target"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={editForm.labor_target_percent}
                    onChange={(e) => setEditForm(prev => ({ ...prev, labor_target_percent: e.target.value }))}
                    placeholder="e.g. 28"
                    className="pl-9"
                    data-testid="edit-labor-target-input"
                  />
                </div>
                <p className="text-xs text-slate-400">Target labor cost as a percentage of revenue</p>
              </div>

              {editForm.budget_type === 'manual' ? (
                <div className="space-y-2">
                  <Label htmlFor="edit-weekly-budget" className="text-xs text-slate-600">Weekly Labor Budget ($)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      id="edit-weekly-budget"
                      type="number"
                      step="1"
                      min="0"
                      value={editForm.weekly_labor_budget}
                      onChange={(e) => setEditForm(prev => ({ ...prev, weekly_labor_budget: e.target.value }))}
                      placeholder="e.g. 5000"
                      className="pl-9"
                      data-testid="edit-weekly-budget-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="edit-weekly-revenue" className="text-xs text-slate-600">Estimated Weekly Revenue ($)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      id="edit-weekly-revenue"
                      type="number"
                      step="1"
                      min="0"
                      value={editForm.weekly_revenue_estimate}
                      onChange={(e) => setEditForm(prev => ({ ...prev, weekly_revenue_estimate: e.target.value }))}
                      placeholder="e.g. 20000"
                      className="pl-9"
                      data-testid="edit-weekly-revenue-input"
                    />
                  </div>
                  {editForm.labor_target_percent && editForm.weekly_revenue_estimate && (
                    <p className="text-xs text-emerald-600 font-medium">
                      Calculated budget: ${(parseFloat(editForm.weekly_revenue_estimate) * parseFloat(editForm.labor_target_percent) / 100).toFixed(0)}/week
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingLocation(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                disabled={submitting}
                data-testid="save-location-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Location Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) { setDeletePinInput(''); setDeletePinError(''); setDeletingLocation(null); }
          setDeleteDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle size={20} />
              Confirm Location Deletion
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-1">
              <p className="text-sm font-semibold text-red-800">
                You are about to permanently delete:
              </p>
              <p className="text-sm text-red-700 font-bold">{deletingLocation?.location_name}</p>
              <p className="text-sm text-red-700 mt-1">
                All related data — staff assignments, schedules, time logs, and analytics — will be removed.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700" htmlFor="delete-pin-input">
                Enter the 6-digit Location PIN to confirm
              </Label>
              <Input
                id="delete-pin-input"
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                value={deletePinInput}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setDeletePinInput(val);
                  setDeletePinError('');
                }}
                className={`text-center text-xl tracking-widest font-mono ${deletePinError ? 'border-red-500 focus:ring-red-500' : ''}`}
                data-testid="delete-pin-input"
                autoComplete="off"
              />
              {deletePinError && (
                <p className="text-sm text-red-600 font-medium flex items-center gap-1" data-testid="delete-pin-error">
                  <AlertCircle size={13} />
                  {deletePinError}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-1">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setDeletePinInput('');
                  setDeletePinError('');
                  setDeletingLocation(null);
                }}
                data-testid="cancel-delete-location-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteLocation}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={submitting || deletePinInput.length !== 6}
                data-testid="confirm-delete-location-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
